/**
 * Shared constants and micro-components used across Accidentes sub-modules.
 */
import React from 'react';
import axios from 'axios';
import { Clock, DollarSign, IdCard, Briefcase, Percent } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';

const API = process.env.REACT_APP_BACKEND_URL;

export const GRAVEDAD_COLOR = {
  'Leve': '#4CAF50', 'Grave': '#F97316', 'Muy grave': '#F44336',
  'Fallecimiento': '#7B1FA2', 'Sin clasificar': '#999',
};

export function Stat({ icon, label, value, color, multiline }) {
  return (
    <div className={multiline ? '' : 'flex items-center gap-1.5'}>
      <span className="text-[10px] uppercase font-medium" style={{ color: 'var(--vz-text-muted)' }}>{icon}{icon && ' '}{label}</span>
      <span className={multiline ? 'block mt-0.5' : 'ml-auto'} style={{ color: color || 'var(--vz-text-primary)' }}>{value || '—'}</span>
    </div>
  );
}

export function CorrCard({ color, label, n }) {
  return (
    <div className="rounded-lg p-2 text-center" style={{ background: color + '15', border: `1px solid ${color}40` }}>
      <div className="text-lg font-bold" style={{ color }}>{n}</div>
      <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
    </div>
  );
}

export function WorkerProfileSection({ profile }) {
  if (!profile) return null;
  if (!profile.found) {
    return (
      <div className="pt-2 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>
          Perfil del trabajador
        </p>
        <p className="text-[11px]" style={{ color: '#FF9800' }}>
          No encontrado en LOGA{profile.match_via === 'ambiguous' && ' (ambiguo, varios candidatos)'}.
        </p>
      </div>
    );
  }
  const p = profile.profile;
  const isParcial = (p.jornada || '').toLowerCase().includes('parcial');
  const isPeon = (p.categoria_convenio_label || '').toUpperCase().includes('PEON');
  const isJunior = typeof p.antiguedad_anios === 'number' && p.antiguedad_anios < 1;
  const riskFlags = [];
  if (isPeon) riskFlags.push('Peón');
  if (isParcial) riskFlags.push('Jornada parcial');
  if (isJunior) riskFlags.push('Antigüedad < 1 año');

  return (
    <div className="pt-2 border-t space-y-2" style={{ borderColor: 'var(--vz-border-subtle)' }}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>
          Perfil del trabajador <span className="ml-1 text-[9px] opacity-70" data-testid="profile-via">· vía {profile.match_via}</span>
        </p>
        {riskFlags.length > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
            style={{ background: '#F9731620', color: '#F97316', border: '1px solid #F9731640' }}
            data-testid="profile-risk-flag">
            ⚠ {riskFlags.join(' · ')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Stat icon={<IdCard size={11} />} label="Categoría" value={p.categoria_convenio_label || p.categoria_convenio_codigo} />
        <Stat icon={<Briefcase size={11} />} label="Tipo expediente" value={p.tipo_expediente} />
        <Stat icon={<Clock size={11} />} label="Antigüedad" value={p.antiguedad_anios != null ? `${p.antiguedad_anios} años` : null} color={isJunior ? '#F97316' : null} />
        <Stat icon={<Percent size={11} />} label="Jornada" value={p.jornada + (p.porcentaje_jornada_actual ? ` (${p.porcentaje_jornada_actual}%)` : '')} color={isParcial ? '#FF9800' : null} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Stat label="Convenio" value={p.convenio} />
        <Stat label="Sexo" value={p.sexo} />
        <Stat label="Centro LOGA" value={p.centro_label} multiline />
        <Stat label="Nº personal" value={p.n_personal} />
      </div>
    </div>
  );
}

export function KpiCard({ label, value, color, pct }) {
  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
      <div className="text-2xl font-bold" style={{ color }}>{(value || 0).toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
      {pct !== undefined && <div className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{pct}%</div>}
    </div>
  );
}

export function BajaStat({ label, value, color, suffix = '', subtitle }) {
  return (
    <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="text-xl font-bold" style={{ color }}>
        {(value || 0).toLocaleString('es-ES')}{suffix}
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
      {subtitle && <div className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{subtitle}</div>}
    </div>
  );
}

export function CosteStat({ label, value, color, subtitle, highlight }) {
  return (
    <div className="rounded-lg p-3"
      style={{
        background: highlight ? 'rgba(156,39,176,0.15)' : 'rgba(0,0,0,0.18)',
        border: `1px solid ${highlight ? 'rgba(156,39,176,0.4)' : 'rgba(255,255,255,0.04)'}`,
      }}>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
      {subtitle && <div className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--vz-text-muted)' }} title={subtitle}>{subtitle}</div>}
    </div>
  );
}

export function Panel({ title, children }) {
  return (
    <div className="rounded-xl p-4 space-y-1.5" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
      <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--vz-text-muted)' }}>{title}</h4>
      {children}
    </div>
  );
}

export function Bar({ label, n, max, color }) {
  const pct = max > 0 ? (n / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="text-xs flex-1 truncate" style={{ color: 'var(--vz-text-primary)' }}>{label}</div>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-xs font-mono" style={{ color, minWidth: 30, textAlign: 'right' }}>{n}</div>
    </div>
  );
}

export function BajasPanel({ bajas }) {
  const maxDays = Math.max(1, ...bajas.by_gravedad.map(g => g.dias));
  return (
    <div className="rounded-xl p-5 space-y-4"
      style={{ background: 'linear-gradient(135deg, rgba(244,67,54,0.06) 0%, rgba(255,152,0,0.04) 100%)',
               border: '1px solid rgba(244,67,54,0.25)' }}
      data-testid="bajas-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-white flex items-center gap-2">
            <Clock size={14} style={{ color: '#F44336' }} /> Días de baja acumulados
          </h4>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Solo accidentes <strong>con baja médica</strong>. Los días "en curso" se calculan hasta hoy
            (rellena <em>Fecha alta médica</em> en el formulario SP&S del parte para cerrar la cuenta).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BajaStat label="Días totales" value={bajas.dias_total} color="#F44336" suffix=" días" />
        <BajaStat label="Bajas en curso" value={bajas.bajas_en_curso} color="#FF9800"
          subtitle={`${bajas.dias_en_curso.toLocaleString()} días`} />
        <BajaStat label="Bajas cerradas" value={bajas.bajas_cerradas} color="#4CAF50"
          subtitle={`${bajas.dias_cerrados.toLocaleString()} días`} />
        <BajaStat label="Promedio por baja" value={bajas.promedio_dias} color="#9C27B0" suffix=" días" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
          <h5 className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>Días por gravedad</h5>
          {bajas.by_gravedad.map((g, i) => (
            <Bar key={i} label={`${g.label} (${g.n})`} n={g.dias} max={maxDays} color={GRAVEDAD_COLOR[g.label] || '#999'} />
          ))}
        </div>
        <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
          <h5 className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>Top 5 trabajadores con más días</h5>
          {bajas.top_trabajadores.length === 0 ? (
            <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>Sin datos.</p>
          ) : bajas.top_trabajadores.map((t, i) => (
            <div key={i} className="flex justify-between gap-2 py-1 border-b last:border-0"
              style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{t.trabajador}</div>
                <div className="text-[9px] truncate" style={{ color: 'var(--vz-text-muted)' }}>
                  {t.centro || '—'} · {t.n_partes} parte{t.n_partes > 1 ? 's' : ''}
                </div>
              </div>
              <div className="text-xs font-bold whitespace-nowrap" style={{ color: '#F44336' }}>
                {t.dias_total.toLocaleString()} días
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CostesPanel({ filters, authHeader }) {
  const params = Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => v !== ''));
  const { data, isLoading } = useQuery({
    queryKey: ['acc-costes', params],
    queryFn: () => axios.get(`${API}/api/accidentes-costes`, { headers: authHeader(), params, timeout: 90000 }).then(r => r.data),
  });
  if (isLoading || !data) return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(156,39,176,0.05)', border: '1px solid rgba(156,39,176,0.2)' }}>
      <Skeleton className="h-32 w-full" />
    </div>
  );
  const t = data.totales || {};
  const fmtEur = (n) => '€ ' + (Math.round((n || 0) / 1000)).toLocaleString('es-ES') + 'K';
  const fmtFullEur = (n) => '€ ' + (n || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });

  return (
    <div className="rounded-xl p-5 space-y-4"
      style={{ background: 'linear-gradient(135deg, rgba(156,39,176,0.06) 0%, rgba(74,158,255,0.04) 100%)',
               border: '1px solid rgba(156,39,176,0.25)' }}
      data-testid="costes-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-white flex items-center gap-2">
            <DollarSign size={14} style={{ color: '#9C27B0' }} /> Costes ocultos de la siniestralidad
          </h4>
          <p className="text-[11px] mt-0.5 max-w-3xl" style={{ color: 'var(--vz-text-muted)' }}>
            Estimación basada en <strong>convenio LVMAD 2026</strong>: <code>coste = días × salario_diario × {((1 + (data.params?.factor_ss || 0)) * 100 / 100).toFixed(2)}</code> (incluye SS patronal {Math.round((data.params?.factor_ss || 0) * 100)}%) +
            indirectos {data.params?.factor_indirecto}× (<em>Iceberg de Heinrich</em>).
            Categoría profesional cruzada con LOGA. {t.salario_default_aplicado > 0 && (
              <span style={{ color: '#FF9800' }}>{t.salario_default_aplicado} parte(s) usan salario default {data.params?.salario_default}€/día.</span>
            )}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CosteStat label="Coste DIRECTO" value={fmtEur(t.coste_directo)} color="#F44336"
          subtitle={`${(t.dias_total || 0).toLocaleString('es-ES')} días × salario × 1.30 SS`} />
        <CosteStat label="Coste INDIRECTO" value={fmtEur(t.coste_indirecto)} color="#FF9800"
          subtitle={`Iceberg ${data.params?.factor_indirecto}× directo`} />
        <CosteStat label="COSTE TOTAL ESTIMADO" value={fmtEur(t.coste_total)} color="#9C27B0"
          subtitle={fmtFullEur(t.coste_total)} highlight />
        <CosteStat label="Coste medio por baja" value={fmtEur(t.coste_total / Math.max(t.n_bajas, 1))} color="#4A9EFF"
          subtitle={`${t.n_bajas} bajas (${t.bajas_en_curso} en curso)`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
          <h5 className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>Coste por gravedad</h5>
          {data.by_gravedad.map((g, i) => {
            const max = Math.max(...data.by_gravedad.map(x => x.coste_total));
            return <Bar key={i} label={`${g.label} (${g.n} · ${g.dias.toLocaleString('es-ES')}d)`} n={g.coste_total} max={max} color={GRAVEDAD_COLOR[g.label] || '#999'} />;
          })}
        </div>
        <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
          <h5 className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>Top 5 centros con mayor coste</h5>
          {data.top_centros.slice(0, 5).map((s, i) => (
            <div key={i} className="flex justify-between gap-2 py-1 border-b last:border-0"
              style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{s.label}</div>
                <div className="text-[9px]" style={{ color: 'var(--vz-text-muted)' }}>{s.n} bajas · {s.dias.toLocaleString('es-ES')} días</div>
              </div>
              <div className="text-xs font-bold whitespace-nowrap" style={{ color: '#9C27B0' }}>{fmtEur(s.coste_total)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
        <h5 className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-muted)' }}>Coste por categoría profesional (top 5)</h5>
        <div className="space-y-1.5">
          {data.by_categoria.slice(0, 5).map((c, i) => (
            <div key={i} className="flex items-center gap-3 text-[11px]">
              <div className="w-56 truncate" style={{ color: 'var(--vz-text-primary)' }} title={c.label}>{c.label}</div>
              <div className="text-[9px] tabular-nums" style={{ color: 'var(--vz-text-muted)' }}>
                {c.salario_dia.toFixed(0)}€/día · {c.n} bajas · {c.dias.toLocaleString('es-ES')}d
              </div>
              <div className="ml-auto font-bold tabular-nums" style={{ color: '#9C27B0' }}>{fmtEur(c.coste_total)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function KpiPill({ label, value }) {
  return (
    <div className="px-3 py-1.5 rounded-md text-center" style={{ background: 'rgba(74,158,255,0.12)' }}>
      <div className="text-sm font-bold" style={{ color: '#4A9EFF' }}>{value}</div>
      <div className="text-[9px] uppercase" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
    </div>
  );
}

export function MarkdownView({ md }) {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) { out.push(<ul key={out.length} className="list-disc pl-5 space-y-1 my-2">{listBuf.map((x, i) => <li key={i}>{x}</li>)}</ul>); listBuf = []; }
  };
  lines.forEach((l, i) => {
    if (l.startsWith('## ')) { flushList(); out.push(<h3 key={i} className="text-sm font-bold mt-4 mb-2" style={{ color: '#4A9EFF' }}>{l.slice(3)}</h3>); }
    else if (l.startsWith('### ')) { flushList(); out.push(<h4 key={i} className="text-xs font-bold mt-3 mb-1.5">{l.slice(4)}</h4>); }
    else if (l.startsWith('- ') || l.startsWith('* ')) { listBuf.push(l.slice(2)); }
    else if (l.trim() === '') { flushList(); }
    else { flushList(); out.push(<p key={i} className="my-2 leading-relaxed">{l}</p>); }
  });
  flushList();
  return <>{out}</>;
}

export function ResStat({ label, value, color }) {
  return (
    <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="text-lg font-bold" style={{ color }}>{(value || 0).toLocaleString('es-ES')}</div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
    </div>
  );
}

export function parseBulkPaste(raw) {
  const out = { valid: [], invalid: [], preview: [] };
  if (!raw || !raw.trim()) return out;
  const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  for (const line of lines) {
    const cells = line.split(/\t|;|,/).map(c => c.trim()).filter(Boolean);
    if (cells.length < 2) {
      out.invalid.push({ ref: line, error: 'esperaba 2 columnas' });
      out.preview.push({ ref: line, fecha_raw: '', ok: false, error: 'esperaba 2 columnas' });
      continue;
    }
    const lower = cells.map(c => c.toLowerCase());
    if (lower.some(c => c.includes('referencia') || c.includes('ref') || c.includes('alta') || c.includes('fecha'))) {
      if (lower.every(c => !/^\d/.test(c) && !/^dlt-/i.test(c))) continue;
    }
    let refIdx = cells.findIndex(c => /^DLT-/i.test(c));
    if (refIdx === -1) refIdx = cells.findIndex(c => /^[A-Z0-9-]{8,}$/i.test(c) && !/[\/.]/.test(c));
    if (refIdx === -1) refIdx = 0;
    const dateIdx = refIdx === 0 ? 1 : 0;
    const ref = cells[refIdx];
    const fechaRaw = cells[dateIdx];
    const fechaIso = parseDateAny(fechaRaw);
    if (!ref || !fechaIso) {
      out.invalid.push({ ref, fecha_raw: fechaRaw, error: !fechaIso ? 'fecha no reconocida' : 'ref vacío' });
      out.preview.push({ ref, fecha_raw: fechaRaw, ok: false, error: !fechaIso ? 'fecha inválida' : 'ref vacío' });
      continue;
    }
    out.valid.push({ n_referencia: ref, fecha_alta_medica: fechaIso });
    out.preview.push({ ref, fecha_raw: fechaRaw, fecha_iso: fechaIso, ok: true });
  }
  if (out.preview.length > 200) out.preview = out.preview.slice(0, 200);
  return out;
}

export function parseDateAny(s) {
  if (!s) return null;
  s = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (!m) return null;
  let [, d, mo, y] = m;
  if (y.length === 2) y = '20' + y;
  d = d.padStart(2, '0'); mo = mo.padStart(2, '0');
  if (parseInt(d) > 31 || parseInt(mo) > 12) return null;
  return `${y}-${mo}-${d}`;
}
